import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, Loader2, Target } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { getImpact, type ImpactEdge } from '../api/symbols.js';
import { useMcp } from '../api/use-mcp.js';
import { PageHeader } from '../components/page-header.js';
import { cn } from '../lib/cn.js';

function riskLevel(edges: number): 'low' | 'medium' | 'high' {
  if (edges <= 5) return 'low';
  if (edges <= 25) return 'medium';
  return 'high';
}

const RISK_STYLES: Record<'low' | 'medium' | 'high', string> = {
  low: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300',
  medium: 'border-amber-500/30 bg-amber-500/15 text-amber-300',
  high: 'border-rose-500/30 bg-rose-500/15 text-rose-300',
};

function RiskBadge({ count }: { readonly count: number }): JSX.Element {
  const level = riskLevel(count);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
        RISK_STYLES[level],
      )}
    >
      <span className="font-mono uppercase tracking-wider">{level}</span>
      <span className="text-text-muted">·</span>
      <span>{count} edges</span>
    </span>
  );
}

function EdgeTable({
  title,
  icon,
  edges,
}: {
  readonly title: string;
  readonly icon: React.ReactNode;
  readonly edges: readonly ImpactEdge[];
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-2xl border border-border-subtle bg-surface/60 backdrop-blur">
      <header className="flex items-center justify-between gap-2 border-b border-dashed border-border-subtle px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          {icon}
          {title}
        </h3>
        <RiskBadge count={edges.length} />
      </header>
      {edges.length === 0 ? (
        <p className="px-4 py-6 text-sm text-text-muted">No edges.</p>
      ) : (
        <div className="scrollbar-thin overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-subtle bg-deep/60 text-left font-mono text-[10px] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2">From</th>
                <th className="px-4 py-2">To</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Hop</th>
                <th className="px-4 py-2">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle font-mono text-text-secondary">
              {edges.map((e, i) => (
                <tr key={`${e.fromId}-${e.toId}-${String(i)}`} className="hover:bg-hover/40">
                  <td className="px-4 py-2 text-text-primary">{e.fromId}</td>
                  <td className="px-4 py-2 text-text-primary">{e.toId}</td>
                  <td className="px-4 py-2 text-accent">{e.type}</td>
                  <td className="px-4 py-2">{e.hop}</td>
                  <td className="px-4 py-2">{e.confidence.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ImpactAnalyzer(): JSX.Element {
  const { name } = useParams<{ name: string }>();
  const { handle, ready } = useMcp();
  const [target, setTarget] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [depth, setDepth] = useState(2);

  const impact = useQuery({
    queryKey: ['mcp-impact', name, submitted, depth],
    queryFn: () => getImpact(handle!, { repo: name ?? '', target: submitted, depth }),
    enabled: ready && submitted.length > 0 && Boolean(name),
  });

  if (!name) return <p className="p-8 text-text-muted">Missing repo.</p>;

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
      <PageHeader
        eyebrow={name}
        title="Impact analyzer"
        subtitle="Trace upstream/downstream call ripples for any symbol."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(target);
        }}
        className="flex flex-wrap items-center gap-3 rounded-2xl border border-border-subtle bg-surface/60 p-3 backdrop-blur"
      >
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border-subtle bg-deep px-3 py-2 transition-all focus-within:border-accent/40 focus-within:ring-2 focus-within:ring-accent/20">
          <Target className="h-4 w-4 text-text-muted" />
          <input
            type="search"
            placeholder="Symbol id (e.g. svc.UserService.create)"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            aria-label="Impact target"
            className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-border-subtle bg-deep px-3 py-2 text-xs text-text-secondary">
          Depth
          <input
            type="range"
            min={1}
            max={5}
            value={depth}
            onChange={(e) => setDepth(Number(e.target.value))}
            aria-label="Depth"
            className="accent-accent"
          />
          <span className="w-6 text-right font-mono text-text-primary">{depth}</span>
        </label>
        <button
          type="submit"
          disabled={!ready}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-accent to-accent-dim px-4 py-2 text-sm font-semibold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          Analyze
        </button>
      </form>

      {impact.isFetching && (
        <p className="flex items-center gap-2 font-mono text-xs text-text-secondary">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
          Computing impact…
        </p>
      )}
      {impact.error && (
        <p className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
          {String(impact.error)}
        </p>
      )}
      {impact.data && (
        <div className="grid gap-4">
          <EdgeTable
            title="Upstream (callers)"
            icon={<ArrowUpRight className="h-4 w-4 text-emerald-400" />}
            edges={impact.data.upstream}
          />
          <EdgeTable
            title="Downstream (callees)"
            icon={<ArrowDownRight className="h-4 w-4 text-blue-400" />}
            edges={impact.data.downstream}
          />
        </div>
      )}
    </section>
  );
}
